import { useState, useRef } from 'react'
import { PDFDocument, degrees } from 'pdf-lib'
import { Upload, Download, RotateCw, FileText } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFRotator() {
  const [file, setFile] = useState(null)
  const [rotatedPdf, setRotatedPdf] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [rotation, setRotation] = useState(90)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setRotatedPdf(null)
    } else if (selectedFile) {
      alert('Please select a PDF file')
    }
  }

  const rotatePDF = async () => {
    if (!file) {
      alert('Please select a PDF file')
      return
    }

    setProcessing(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      const pages = pdfDoc.getPages()

      pages.forEach(page => {
        page.setRotation(degrees(rotation))
      })

      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setRotatedPdf({ url, blob })
    } catch (error) {
      alert('Error rotating PDF: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const downloadRotated = () => {
    if (!rotatedPdf) return
    const link = document.createElement('a')
    link.href = rotatedPdf.url
    link.download = `rotated-${file.name}`
    link.click()
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              {file ? 'Change PDF' : 'Upload PDF'}
            </Button>
            {file && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-gray-900">{file.name}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Rotation Angle:</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="270"
                step="90"
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 text-center">
                <span className="text-lg font-bold text-gray-900">{rotation}°</span>
              </div>
            </div>
            <div className="flex justify-between mt-2">
              <button
                onClick={() => setRotation(0)}
                className={`px-3 py-1 rounded text-sm ${rotation === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}
              >
                0°
              </button>
              <button
                onClick={() => setRotation(90)}
                className={`px-3 py-1 rounded text-sm ${rotation === 90 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}
              >
                90°
              </button>
              <button
                onClick={() => setRotation(180)}
                className={`px-3 py-1 rounded text-sm ${rotation === 180 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}
              >
                180°
              </button>
              <button
                onClick={() => setRotation(270)}
                className={`px-3 py-1 rounded text-sm ${rotation === 270 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}
              >
                270°
              </button>
            </div>
          </div>

          <Button
            onClick={rotatePDF}
            variant="primary"
            className="w-full"
            disabled={!file || processing}
          >
            <RotateCw className="w-5 h-5 mr-2" />
            {processing ? 'Rotating PDF...' : 'Rotate PDF'}
          </Button>
        </Card.Body>
      </Card>

      {rotatedPdf && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <p className="text-green-600 font-semibold">PDF rotated successfully!</p>
              <Button onClick={downloadRotated} variant="primary" size="lg" className="w-full">
                <Download className="w-5 h-5 mr-2" />
                Download Rotated PDF
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!file && (
        <Card>
          <Card.Body className="text-center py-12">
            <RotateCw className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Upload a PDF file to rotate its pages</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

